import {
  STAFF_IMPORT_BOUNDS,
  type StaffImportExtension,
  type StaffImportParseError,
  staffImportExtensions,
} from "@lafayette/schemas";

// One file upload as a small state machine. The flow:
//   requesting -> ask the API for a signed upload target (W1)
//   uploading  -> send the file straight to storage (the browser parses NOTHING)
//   reading    -> ask the API to read the stored file (P1)
//   ready      -> the server answered with a preview; failed -> nothing was saved
// The reducer is pure. The component runs the effect of each state and sends the next event. A failure
// carries a sentence chosen from a kind or a code, never the file name, a header or a cell.

export type UploadStage = "requesting" | "uploading" | "reading";
export type UploadFailureStage = UploadStage | "rejected";

type Source = Readonly<{
  fileName: string;
  byteSize: number;
  extension: StaffImportExtension | null;
}>;

export type UploadState =
  | Readonly<{ status: "idle" }>
  | (Source & Readonly<{ status: UploadStage | "ready"; objectPath: string | null }>)
  | (Source &
      Readonly<{
        status: "failed";
        objectPath: string | null;
        failedAt: UploadFailureStage;
        message: string;
        retryable: boolean;
        parseErrors: readonly StaffImportParseError[];
      }>);

export type UploadEvent =
  | Readonly<{ type: "choose"; file: Readonly<{ name: string; size: number }> }>
  | Readonly<{ type: "target_received"; objectPath: string }>
  | Readonly<{ type: "uploaded" }>
  | Readonly<{ type: "read" }>
  | Readonly<{
      type: "failed";
      message: string;
      retryable: boolean;
      parseErrors?: readonly StaffImportParseError[];
    }>
  | Readonly<{ type: "retry" }>
  | Readonly<{ type: "reset" }>;

export const IDLE_UPLOAD: UploadState = { status: "idle" };

export type FileCheck =
  | Readonly<{ ok: true; extension: StaffImportExtension }>
  | Readonly<{ ok: false; message: string }>;

const MAX_MEGABYTES = STAFF_IMPORT_BOUNDS.maxBytes / (1024 * 1024);

export function extensionOf(fileName: string): StaffImportExtension | null {
  const dot = fileName.lastIndexOf(".");
  if (dot < 0) return null;
  const extension = fileName.slice(dot + 1).toLowerCase();
  return staffImportExtensions.find((allowed) => allowed === extension) ?? null;
}

/** The checks the browser can do before it asks for a target. They are fast feedback only: the server checks again. */
export function checkImportFile(file: Readonly<{ name: string; size: number }>): FileCheck {
  const extension = extensionOf(file.name);
  if (extension === null) {
    return { ok: false, message: "Only .csv, .tsv, .xlsx and .xls files can be uploaded." };
  }
  if (file.size < 1) return { ok: false, message: "The file is empty." };
  if (file.size > STAFF_IMPORT_BOUNDS.maxBytes) {
    return { ok: false, message: `The file is larger than ${MAX_MEGABYTES} MB.` };
  }
  return { ok: true, extension };
}

/** The first state of a file. A file the browser refuses never reaches the API. */
export function startUpload(file: Readonly<{ name: string; size: number }>): UploadState {
  const check = checkImportFile(file);
  if (!check.ok) {
    return {
      status: "failed",
      fileName: file.name,
      byteSize: file.size,
      extension: null,
      objectPath: null,
      failedAt: "rejected",
      message: check.message,
      retryable: false,
      parseErrors: [],
    };
  }
  return {
    status: "requesting",
    fileName: file.name,
    byteSize: file.size,
    extension: check.extension,
    objectPath: null,
  };
}

/** A failed upload restarts at the stage that failed. A refused file cannot be retried. */
export function retryStage(state: UploadState): UploadStage | null {
  if (state.status !== "failed" || !state.retryable || state.failedAt === "rejected") return null;
  if (state.failedAt === "reading" && state.objectPath !== null) return "reading";
  return "requesting";
}

export function uploadReducer(state: UploadState, event: UploadEvent): UploadState {
  if (event.type === "reset") return IDLE_UPLOAD;
  // A new file replaces whatever came before, a failed or a finished upload included.
  if (event.type === "choose") return startUpload(event.file);
  if (state.status === "idle") return state;
  switch (event.type) {
    case "target_received":
      return state.status === "requesting"
        ? { ...state, status: "uploading", objectPath: event.objectPath }
        : state;
    case "uploaded":
      return state.status === "uploading" ? { ...state, status: "reading" } : state;
    case "read":
      return state.status === "reading" ? { ...state, status: "ready" } : state;
    case "failed": {
      if (state.status === "failed" || state.status === "ready") return state;
      return {
        status: "failed",
        fileName: state.fileName,
        byteSize: state.byteSize,
        extension: state.extension,
        objectPath: state.objectPath,
        failedAt: state.status,
        message: event.message,
        retryable: event.retryable,
        parseErrors: event.parseErrors ?? [],
      };
    }
    case "retry": {
      const stage = retryStage(state);
      if (stage === null || state.status !== "failed") return state;
      return {
        status: stage,
        fileName: state.fileName,
        byteSize: state.byteSize,
        extension: state.extension,
        // A retry of the read keeps the path. A retry of an earlier stage starts over with a new target.
        objectPath: stage === "reading" ? state.objectPath : null,
      };
    }
  }
}

/** A short status for a screen reader. It holds no file name. */
export function describeUpload(state: UploadState): string {
  switch (state.status) {
    case "idle":
      return "No file chosen";
    case "requesting":
      return "Preparing the upload";
    case "uploading":
      return "Uploading the file";
    case "reading":
      return "Reading the file";
    case "ready":
      return "The file was read";
    case "failed":
      return `Failed: ${state.message}`;
  }
}

export function isUploadActive(state: UploadState): boolean {
  return (
    state.status === "requesting" || state.status === "uploading" || state.status === "reading"
  );
}
